import React, { useMemo } from 'react';
import UnifiedCalendarItem from './UnifiedCalendarItem';
import type { UnifiedCalendarItemProps } from './UnifiedCalendarItem';

type CalendarEntry = UnifiedCalendarItemProps['entry'];

interface CalendarDayViewProps {
  entries: CalendarEntry[];
  selectedDate: Date | string;
  isDark: boolean;
  cardClass: string;
  onDateChange: (date: Date) => void;
  onEntryClick?: (entry: CalendarEntry) => void;
}

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function toDate(d: Date | string): Date {
  if (d instanceof Date) return d;
  // Parse YYYY-MM-DD as local date to avoid UTC shift
  const [y, m, day] = d.split('-').map(Number);
  if (y && m && day) return new Date(y, m - 1, day);
  return new Date(d);
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const CalendarDayView: React.FC<CalendarDayViewProps> = ({
  entries,
  selectedDate,
  isDark,
  cardClass,
  onDateChange,
  onEntryClick,
}) => {
  const date = useMemo(() => toDate(selectedDate), [selectedDate]);

  const dayEntries = useMemo(() => {
    return entries.filter((entry) => {
      try {
        const entryDate = toDate(entry.dueDate);
        return isSameDay(entryDate, date);
      } catch {
        return false;
      }
    });
  }, [entries, date]);

  const goToPrev = () => {
    const prev = new Date(date);
    prev.setDate(date.getDate() - 1);
    onDateChange(prev);
  };

  const goToNext = () => {
    const next = new Date(date);
    next.setDate(date.getDate() + 1);
    onDateChange(next);
  };

  const goToToday = () => {
    onDateChange(new Date());
  };

  const isToday = isSameDay(date, new Date());

  const formattedDate = `${DAY_NAMES[date.getDay()]}, ${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

  const navBtnClass = isDark
    ? 'px-3 py-1.5 rounded text-sm font-medium bg-slate-700 text-slate-200 hover:bg-slate-600 transition-colors'
    : 'px-3 py-1.5 rounded text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors';

  const emptyTextClass = isDark ? 'text-slate-400' : 'text-slate-500';

  const dateInputValue = toLocalDateKey(date);

  const handleDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val) {
      const [y, m, d] = val.split('-').map(Number);
      onDateChange(new Date(y, m - 1, d));
    }
  };

  return (
    <div className={`${cardClass} rounded-xl p-4 flex flex-col gap-4`}>
      {/* Header: navigation + date */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goToPrev}
            className={navBtnClass}
            aria-label="Previous day"
          >
            &#8249;
          </button>
          <button
            type="button"
            onClick={goToNext}
            className={navBtnClass}
            aria-label="Next day"
          >
            &#8250;
          </button>
          {!isToday && (
            <button
              type="button"
              onClick={goToToday}
              className={`${navBtnClass} ml-1`}
            >
              Today
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <h2
            className={`text-base sm:text-lg font-semibold ${
              isDark ? 'text-white' : 'text-slate-900'
            } ${isToday ? 'text-blue-500' : ''}`}
          >
            {formattedDate}
          </h2>

          {/* Date picker */}
          <input
            type="date"
            value={dateInputValue}
            onChange={handleDateInput}
            className={`text-sm rounded px-2 py-1 border ${
              isDark
                ? 'bg-slate-700 text-slate-200 border-slate-600'
                : 'bg-white text-slate-700 border-slate-300'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            aria-label="Select date"
          />
        </div>
      </div>

      {/* Entry count badge */}
      <div className="flex items-center gap-2">
        <span
          className={`text-sm font-medium ${
            isDark ? 'text-slate-300' : 'text-slate-600'
          }`}
        >
          {dayEntries.length === 0
            ? 'No items'
            : `${dayEntries.length} item${dayEntries.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Entry list */}
      {dayEntries.length === 0 ? (
        <div
          className={`py-10 text-center text-sm ${emptyTextClass} border-2 border-dashed rounded-lg ${
            isDark ? 'border-slate-600' : 'border-slate-200'
          }`}
        >
          No calendar items for this day.
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {dayEntries.map((entry) => (
            <li key={`${entry.type}-${entry.id}`}>
              <UnifiedCalendarItem
                entry={entry}
                isDark={isDark}
                onClick={onEntryClick ? () => onEntryClick(entry) : undefined}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CalendarDayView;
